import { useState } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { Editor } from '@/components/Editor';

/**
 * Guide data from the backend.
 */
interface Guide {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    content: string;
    icon: string;
    is_published: boolean;
}

/**
 * Props for the Edit page.
 */
interface Props {
    guide: Guide;
}

/**
 * GM University Edit - Form to edit an existing guide.
 */
export default function Edit({ guide }: Props) {
    const [title, setTitle] = useState(guide.title);
    const [description, setDescription] = useState(guide.description || '');
    const [content, setContent] = useState(guide.content);
    const [icon, setIcon] = useState(guide.icon);
    const [isPublished, setIsPublished] = useState(guide.is_published);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.patch(`/gm/university/${guide.id}`, {
            title,
            description: description || null,
            content,
            icon,
            is_published: isPublished,
        }, {
            onError: (errors) => setErrors(errors),
            onFinish: () => setIsSubmitting(false),
        });
    };

    return (
        <GmLayout title={`Edit: ${guide.title}`} activePath="/gm/university">
            <div className="max-w-4xl">
                <div className="flex items-center gap-4 mb-6">
                    <Link
                        href="/gm/university"
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        ← Back
                    </Link>
                    <h1 className="text-2xl font-bold text-white">Edit Guide</h1>
                    <a
                        href={`/university/${guide.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 text-sm bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                    >
                        View Guide →
                    </a>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Title and Icon */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-3">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Title *
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g., Adventuring Parties"
                                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                            {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Icon
                            </label>
                            <input
                                type="text"
                                value={icon}
                                onChange={(e) => setIcon(e.target.value)}
                                placeholder="📚"
                                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl"
                            />
                        </div>
                    </div>

                    {/* Slug (read-only) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Slug
                        </label>
                        <input
                            type="text"
                            value={guide.slug}
                            disabled
                            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-gray-400 cursor-not-allowed"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            URL: /university/{guide.slug}
                        </p>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Description
                        </label>
                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description shown in the guide list"
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.description && <p className="mt-1 text-sm text-red-400">{errors.description}</p>}
                    </div>

                    {/* Content */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Content (Markdown) *
                        </label>
                        <Editor
                            value={content}
                            onChange={(value) => setContent(value)}
                            placeholder="Write your guide content using Markdown..."
                            height={500}
                        />
                        {errors.content && <p className="mt-1 text-sm text-red-400">{errors.content}</p>}
                    </div>

                    {/* Published */}
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="is_published"
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="is_published" className="text-sm text-gray-300">
                            Published (visible to all players)
                        </label>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-4 pt-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                        <Link
                            href="/gm/university"
                            className="px-6 py-2 text-gray-300 hover:text-white transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </GmLayout>
    );
}
